import { useState } from "react";
import { Dialog } from "./Dialog.tsx";

/**
 * Окно «завести вещь, у которой есть только название»: ученик, заготовка,
 * группа.
 *
 * ⚠️ **Заведён 14.09.2026 вместе с переездом заведения в главное действие
 * экрана** (подход 2е переноса дизайн-системы). До этого дня на трёх экранах
 * стояла форма из одного поля с кнопкой НАД списком, и агент, проходивший
 * стенд, принял её за поиск: одиночное поле с плейсхолдером и кнопкой рядом
 * означает фильтр в любом другом приложении. Место над списком занял поиск,
 * а заведение стало тем, чем оно и является: главным действием экрана, то есть
 * залитой кнопкой в правом конце строки заголовка.
 *
 * ⚠️ **Один компонент на три экрана, а не три похожих.** Разница между ними
 * сводилась к трём словам (заголовок, подпись поля, подпись кнопки) и потолку
 * длины, а расходятся такие копии первой же правкой: у одной остаётся отправка
 * по Enter, у другой пропадает, у третьей кнопка не гаснет на пустом поле.
 *
 * ⚠️ **Настоящий `<form>`, а не кнопка с обработчиком.** Отправка по Enter из
 * поля это то, чем заводят десять учеников подряд, и держится она формой, а не
 * нашим `onKeyDown`. Ровно поэтому подвал стоит ВНУТРИ формы (`.form__foot`), а
 * не в `foot` окна: вынесенная наружу кнопка теряет свою форму, и вместе с ней
 * теряется Enter. Правило подвала при этом буквально одно, оно записано у
 * `.form__foot` в `styles.css`, так что человек разницы не видит.
 *
 * ⚠️ **Окно не закрывает себя само.** Закрыть его решает экран: у ученика после
 * заведения показывается ссылка-приглашение в строке списка, у группы происходит
 * переход на её страницу, у заготовки список просто перечитывается. Окно, которое
 * закрывается само, отняло бы у экрана возможность показать отказ сервера.
 */
export function NameDialog(props: {
  /** Заголовок окна: «Новый ученик», «Новая заготовка», «Новая группа». */
  title: string;
  /** Подпись поля: тем, ЧТО вводят. «Имя ученика», «Название заготовки». */
  label: string;
  /** Подпись кнопки подтверждения. */
  submit: string;
  /** Потолок длины, тот же, что у сервера: обрезать он не обязан, отказать может. */
  maxLength: number;
  /**
   * Строка под полем: то, чего человек не может знать сам. У ученика это
   * свойства ссылки, у группы это видимость имён. `undefined` означает «сказать
   * нечего», и тогда слота нет вовсе.
   */
  hint?: string;
  /** Отказ сервера словами. Остаётся в окне: исправляют его здесь же. */
  err: string | null;
  busy: boolean;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const ready = name.trim() !== "" && !props.busy;

  return (
    <Dialog title={props.title} onClose={props.onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) props.onSubmit(name.trim());
        }}
      >
        {props.err !== null && <p className="err">{props.err}</p>}

        <label className="field">
          <span className="lbl">{props.label}</span>
          <input
            className="fld"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={props.maxLength}
            disabled={props.busy}
            /* Фокус в поле: окно открыли ровно затем, чтобы в него написать */
            autoFocus
          />
          {props.hint !== undefined && <span className="hint">{props.hint}</span>}
        </label>

        {/* Главная кнопка крайняя правая, тихая «Отмена» левее неё */}
        <div className="form__foot">
          <button type="button" className="btn btn--quiet" onClick={props.onClose}>
            Отмена
          </button>
          <button type="submit" className="btn btn--primary" disabled={!ready}>
            {props.submit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
